import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { httpRequest } from "../redux/Action";
import { API_END_POINTS } from "../api/ApiEndPonts";
import { IoSearch, IoDownloadOutline, IoChevronDown, IoChevronUp, IoPersonOutline, IoTimeOutline } from "react-icons/io5";
import { motion, AnimatePresence } from "framer-motion";
import * as XLSX from "xlsx";

function UserTracking() {
  const dispatch = useDispatch();

  const [userId, setUserId] = useState("");
  const [history, setHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [search, setSearch] = useState("");
  const [days, setDays] = useState(30);
  const [limit, setLimit] = useState(50);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(false);
  const [userQuery, setUserQuery] = useState("");
  const [users, setUsers] = useState([]);

  const fetchHistory = () => {
    if (!userId) return alert("Please select a user first");
    setLoading(true);
    try {
      dispatch(
        httpRequest({
          key: API_END_POINTS.getUserRouteHistoryAdmin.KEY,
          params: { userId, days, limit },
          onSuccess: (res) => {
            const data = res.data?.history || [];
            setHistory(data);
            setFilteredHistory(data);
            setLoading(false);
          },
          onError: () => setLoading(false),
        })
      );
    } catch (error) {
      setLoading(false);
      console.error("Error fetching history:", error);
    }
  };

  useEffect(() => {
    const filtered = history.filter((item) =>
      item.route.toLowerCase().includes(search.toLowerCase())
    );
    setFilteredHistory(filtered);
  }, [search, history]);

  const downloadExcel = () => {
    const data = filteredHistory.map((item, i) => ({
      "Sr No": i + 1,
      User: userId,
      Route: item.route,
      Time: new Date(item.visitedAt).toLocaleString(),
      IP: item.ipAddress || "-",
      Device: item.device || "-",
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "History");
    XLSX.writeFile(wb, `User_History_${userId}.xlsx`);
  };

  const searchUserHandler = (value) => {
    setUserQuery(value);
    if (!value) {
      setUsers([]);
      return;
    }
    dispatch(
      httpRequest({
        key: API_END_POINTS.searchUsers.KEY,
        params: { query: value },
        onSuccess: (res) => setUsers(res.data || []),
        onError: () => setUsers([]),
      })
    );
  };

  const selectUser = (user) => {
    setUserId(user._id);
    setUserQuery(`${user.name} (${user.email})`);
    setUsers([]);
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div>
          <h2 style={styles.title}>User Activity Tracking</h2>
          <p style={styles.subtitle}>Monitor user journeys and route engagement</p>
        </div>
        <button style={styles.buttonPurple} onClick={downloadExcel} disabled={!filteredHistory.length}>
          <IoDownloadOutline size={18} /> Export Excel
        </button>
      </header>

      {/* ACTION CARD */}
      <div style={styles.card}>
        <div style={styles.filterGrid}>
          <div style={{ position: "relative", flex: 2 }}>
            <label style={styles.label}>Select User</label>
            <input
              style={styles.input}
              placeholder="Search by name or email..."
              value={userQuery}
              onChange={(e) => searchUserHandler(e.target.value)}
            />
            <AnimatePresence>
              {users.length > 0 && (
                <motion.div 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  style={styles.dropdown}
                >
                  {users.map((u) => (
                    <div key={u._id} style={styles.dropdownItem} onClick={() => selectUser(u)}>
                      <IoPersonOutline /> <span>{u.name}</span> <small style={{color: '#64748b'}}>{u.email}</small>
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div style={{ flex: 1 }}>
            <label style={styles.label}>Time Range</label>
            <select style={styles.input} value={days} onChange={(e) => setDays(Number(e.target.value))}>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>

          <div style={{ flex: 1 }}>
            <label style={styles.label}>Limit</label>
            <select style={styles.input} value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
              <option value={10}>10 records</option>
              <option value={50}>50 records</option>
              <option value={100}>100 records</option>
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button style={styles.buttonPrimary} onClick={fetchHistory}>
              {loading ? "..." : <><IoSearch /> Fetch</>}
            </button>
          </div>
        </div>
      </div>

      {/* TABLE SECTION */}
      <div style={styles.card}>
        <div style={styles.tableHeaderActions}>
           <h3 style={{ margin: 0, fontSize: '18px' }}>History Logs</h3>
           <input
            style={styles.searchBox}
            placeholder="Filter by route name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {filteredHistory.length > 0 ? (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>#</th>
                  <th style={styles.th}>Route Path</th>
                  <th style={styles.th}>Timestamp</th>
                  <th style={styles.th}>IP Address</th>
                  <th style={{ ...styles.th, textAlign: 'center' }}>Details</th>
                </tr>
              </thead>
              <tbody>
                {filteredHistory.map((item, i) => (
                  <React.Fragment key={i}>
                    <tr style={styles.tr}>
                      <td style={styles.td}>{i + 1}</td>
                      <td style={styles.td}>
                        <code style={styles.routeBadge}>{item.route}</code>
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <IoTimeOutline color="#64748b"/>
                          {new Date(item.visitedAt).toLocaleString()}
                        </div>
                      </td>
                      <td style={styles.td}>{item.ipAddress || "N/A"}</td>
                      <td style={{ ...styles.td, textAlign: 'center' }}>
                        <button
                          style={styles.expandBtn}
                          onClick={() => setExpanded(expanded === i ? null : i)}
                        >
                          {expanded === i ? <IoChevronUp /> : <IoChevronDown />}
                        </button>
                      </td>
                    </tr>
                    {expanded === i && (
                      <tr>
                        <td colSpan="5" style={styles.detailsRow}>
                          <motion.div 
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            style={styles.detailsGrid}
                          >
                            <div style={styles.detailItem}><strong>Device Info:</strong> {item.device || "Unknown"}</div>
                            <div style={styles.detailItem}><strong>Session ID:</strong> {userId}</div>
                            <div style={styles.detailItem}><strong>Full Timestamp:</strong> {item.visitedAt}</div>
                          </motion.div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={styles.noData}>
            <img src="https://cdn-icons-png.flaticon.com/512/7486/7486744.png" width="60" alt="no-data" style={{ opacity: 0.2, marginBottom: '10px' }} />
            <p>{userId ? "No activity logs found for this period." : "Search and select a user to view tracking data."}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserTracking;

const styles = {
  container: {
    padding: "40px 10%",
    background: "#f1f5f9", // Modern light slate
    minHeight: "100vh",
    fontFamily: "'Inter', sans-serif",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "30px",
  },
  title: {
    fontSize: "28px",
    fontWeight: "800",
    color: "#1e293b",
    margin: 0,
  },
  subtitle: {
    color: "#64748b",
    margin: "5px 0 0 0",
  },
  card: {
    background: "#ffffff",
    borderRadius: "16px",
    padding: "24px",
    boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
    marginBottom: "24px",
  },
  filterGrid: {
    display: "flex",
    gap: "20px",
    alignItems: "flex-end",
  },
  label: {
    display: "block",
    fontSize: "12px",
    fontWeight: "600",
    color: "#64748b",
    marginBottom: "8px",
    textTransform: "uppercase",
    letterSpacing: "0.025em",
  },
  input: {
    width: "100%",
    padding: "12px 16px",
    borderRadius: "10px",
    border: "1px solid #e2e8f0",
    fontSize: "14px",
    outline: "none",
    transition: "border 0.2s",
    boxSizing: "border-box",
    '&:focus': { border: "1px solid #3b82f6" }
  },
  buttonPrimary: {
    background: "#2563eb",
    color: "#fff",
    height: "45px",
    padding: "0 24px",
    border: "none",
    borderRadius: "10px",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    transition: "background 0.2s",
  },
  buttonPurple: {
    background: "#fff",
    color: "#7c3aed",
    border: "1px solid #7c3aed",
    padding: "10px 20px",
    borderRadius: "10px",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  dropdown: {
    position: "absolute",
    top: "75px",
    left: 0,
    right: 0,
    background: "#fff",
    borderRadius: "12px",
    boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1)",
    border: "1px solid #e2e8f0",
    zIndex: 100,
    maxHeight: "250px",
    overflowY: "auto",
  },
  dropdownItem: {
    padding: "12px 16px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    fontSize: "14px",
    borderBottom: "1px solid #f1f5f9",
    transition: "background 0.2s",
    '&:hover': { background: "#f8fafc" }
  },
  tableHeaderActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
  },
  searchBox: {
    padding: "10px 16px",
    borderRadius: "10px",
    border: "1px solid #e2e8f0",
    width: "250px",
  },
  tableWrapper: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: "0",
  },
  th: {
    padding: "16px",
    textAlign: "left",
    background: "#f8fafc",
    color: "#64748b",
    fontWeight: "600",
    fontSize: "13px",
    borderBottom: "1px solid #e2e8f0",
  },
  tr: {
    transition: "background 0.2s",
    '&:hover': { background: "#f8fafc" }
  },
  td: {
    padding: "16px",
    fontSize: "14px",
    color: "#334155",
    borderBottom: "1px solid #f1f5f9",
  },
  routeBadge: {
    background: "#eff6ff",
    color: "#2563eb",
    padding: "6px 12px",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: "500",
  },
  expandBtn: {
    background: "#f1f5f9",
    border: "none",
    borderRadius: "6px",
    padding: "6px",
    cursor: "pointer",
    display: "inline-flex",
  },
  detailsRow: {
    background: "#f8fafc",
    padding: "0",
  },
  detailsGrid: {
    padding: "20px",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "15px",
    fontSize: "13px",
  },
  detailItem: {
    color: "#475569",
  },
  noData: {
    textAlign: "center",
    padding: "60px 0",
    color: "#94a3b8",
  },
};